import { useEffect, useState } from 'react'
import {
  Badge,
  Button,
  Eyebrow,
  Field,
  FieldGroup,
  Fieldset,
  Input,
  Label,
  NeutralPanel,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusText,
  Subheading,
  Text,
} from '../../../components/ui'
import { ArrowRightOnRectangleIcon, CheckIcon, ExclamationTriangleIcon, TrashIcon } from '@heroicons/react/20/solid'
import { DEFAULT_OPENROUTER_MODEL } from '../constants'
import { uiClasses } from '../../shared/uiClasses'
import type { FormEvent } from 'react'
import type { OpenRouterSettings, PersonalProfile } from '../types'
import type { ThemePreference } from '../../theme/themePreference'

interface SettingsTabProps {
  accountEmail: string
  agentApiEnabled: boolean
  isConfigured: boolean
  onClearSettings: (model: string) => Promise<void>
  onDeleteAllChats: () => void
  onSignOut: () => void
  onSavePersonalProfile: (profile: PersonalProfile) => void
  onResetLocalStorage: () => void
  onSaveSettings: (nextSettings: OpenRouterSettings) => Promise<void>
  onThemePreferenceChange: (nextPreference: ThemePreference) => void
  personalProfile: PersonalProfile
  settings: OpenRouterSettings
  themePreference: ThemePreference
  signOutLabel: string
}

const THEME_OPTIONS: Array<{ description: string; id: ThemePreference; label: string }> = [
  {
    id: 'system',
    label: 'System',
    description: 'Follow your operating system preference automatically.',
  },
  {
    id: 'light',
    label: 'Light',
    description: 'Use the brighter color palette.',
  },
  {
    id: 'dark',
    label: 'Dark',
    description: 'Use a darker, low-glare palette.',
  },
]
const INSURANCE_PROVIDER_NONE = '__none__'
const INSURANCE_PROVIDER_OTHER = '__other__'
const INSURANCE_PROVIDER_OPTIONS = [
  'Aetna',
  'Aflac',
  'Alignment Health Plan',
  'Ambetter',
  'Amerigroup',
  'Anthem Blue Cross Blue Shield',
  'Blue Cross Blue Shield',
  'Blue Shield of California',
  'CareFirst BlueCross BlueShield',
  'Centene',
  'Cigna Healthcare',
  'Devoted Health',
  'Health Net',
  'Highmark',
  'Humana',
  'Kaiser Permanente',
  'Medicaid',
  'Medicare',
  'Molina Healthcare',
  'Oscar Health',
  'Providence Health Plan',
  'TRICARE',
  'Tufts Health Plan',
  'UnitedHealthcare',
  'VA Health Care',
  'Wellcare',
]
const INSURANCE_PROVIDER_LOOKUP = new Map(INSURANCE_PROVIDER_OPTIONS.map((provider) => [provider.toLowerCase(), provider]))

function isKnownInsuranceProvider(value: string) {
  return INSURANCE_PROVIDER_LOOKUP.has(value.trim().toLowerCase())
}

export default function SettingsTab({
  accountEmail,
  agentApiEnabled,
  isConfigured,
  onClearSettings,
  onDeleteAllChats,
  onSignOut,
  onSavePersonalProfile,
  onResetLocalStorage,
  onSaveSettings,
  onThemePreferenceChange,
  personalProfile,
  settings,
  themePreference,
  signOutLabel,
}: SettingsTabProps) {
  const [apiKeyDraft, setApiKeyDraft] = useState(settings.apiKey)
  const [modelDraft, setModelDraft] = useState(settings.model)
  const [profileDraft, setProfileDraft] = useState<PersonalProfile>(personalProfile)
  const [statusMessage, setStatusMessage] = useState<string | null>(null)
  const [statusTone, setStatusTone] = useState<'danger' | 'info' | 'success'>('success')
  const [isSaving, setIsSaving] = useState(false)
  const [isClearing, setIsClearing] = useState(false)
  const [isSavingProfile, setIsSavingProfile] = useState(false)
  const [isDeletingAllChats, setIsDeletingAllChats] = useState(false)
  const [isResetting, setIsResetting] = useState(false)
  const [isInsuranceProviderOtherSelected, setIsInsuranceProviderOtherSelected] = useState(() => {
    const normalizedValue = personalProfile.insuranceProvider.trim()
    return Boolean(normalizedValue) && !isKnownInsuranceProvider(normalizedValue)
  })

  useEffect(() => {
    setApiKeyDraft(settings.apiKey)
    setModelDraft(settings.model)
  }, [settings.apiKey, settings.model])

  useEffect(() => {
    setProfileDraft(personalProfile)
    const normalizedValue = personalProfile.insuranceProvider.trim()
    setIsInsuranceProviderOtherSelected(Boolean(normalizedValue) && !isKnownInsuranceProvider(normalizedValue))
  }, [personalProfile])

  const isLlmDirty = apiKeyDraft !== settings.apiKey || modelDraft !== settings.model
  const isProfileDirty = JSON.stringify(profileDraft) !== JSON.stringify(personalProfile)
  const insuranceProviderMatch = INSURANCE_PROVIDER_LOOKUP.get(profileDraft.insuranceProvider.trim().toLowerCase()) ?? null
  const insuranceProviderSelectValue = isInsuranceProviderOtherSelected
    ? INSURANCE_PROVIDER_OTHER
    : insuranceProviderMatch ?? ''
  const insuranceProviderCustomValue = isInsuranceProviderOtherSelected ? profileDraft.insuranceProvider : ''

  function updateProfileField<K extends keyof PersonalProfile>(key: K, value: PersonalProfile[K]) {
    setProfileDraft((previous) => ({
      ...previous,
      [key]: value,
    }))
  }

  function handleInsuranceProviderSelect(nextValue: string) {
    if (!nextValue) {
      setIsInsuranceProviderOtherSelected(false)
      updateProfileField('insuranceProvider', '')
      return
    }

    if (nextValue === INSURANCE_PROVIDER_OTHER) {
      setIsInsuranceProviderOtherSelected(true)
      const existingValue = profileDraft.insuranceProvider.trim()
      const nextCustomValue = isKnownInsuranceProvider(existingValue) ? '' : profileDraft.insuranceProvider
      updateProfileField('insuranceProvider', nextCustomValue)
      return
    }

    setIsInsuranceProviderOtherSelected(false)
    updateProfileField('insuranceProvider', nextValue)
  }

  function handleSaveProfile(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setStatusMessage(null)

    if (!profileDraft.fullName.trim()) {
      setStatusTone('danger')
      setStatusMessage('Full name is required.')
      return
    }

    if (!isProfileDirty) {
      setStatusTone('info')
      setStatusMessage('No personal info changes to save.')
      return
    }

    setIsSavingProfile(true)
    try {
      onSavePersonalProfile(profileDraft)
      setStatusTone('success')
      setStatusMessage('Personal information saved.')
    } finally {
      setIsSavingProfile(false)
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setStatusMessage(null)

    const normalizedModel = modelDraft.trim()
    if (!normalizedModel) {
      setStatusTone('danger')
      setStatusMessage('Model is required to call OpenRouter.')
      return
    }

    if (!isLlmDirty) {
      setStatusTone('info')
      setStatusMessage('No changes to save.')
      return
    }

    setIsSaving(true)
    try {
      await onSaveSettings({
        apiKey: apiKeyDraft.trim(),
        model: normalizedModel,
      })

      setStatusTone('success')
      setStatusMessage('LLM settings saved in browser storage.')
    } catch (error) {
      setStatusTone('danger')
      setStatusMessage(error instanceof Error ? error.message : 'Failed to save settings.')
    } finally {
      setIsSaving(false)
    }
  }

  async function handleClear() {
    setStatusMessage(null)
    const normalizedModel = modelDraft.trim() || DEFAULT_OPENROUTER_MODEL

    setIsClearing(true)
    try {
      await onClearSettings(normalizedModel)
      setApiKeyDraft('')
      setModelDraft(normalizedModel)
      setStatusTone('success')
      setStatusMessage('OpenRouter API key cleared.')
    } catch (error) {
      setStatusTone('danger')
      setStatusMessage(error instanceof Error ? error.message : 'Failed to clear settings.')
    } finally {
      setIsClearing(false)
    }
  }

  function handleResetLocalStorage() {
    const shouldReset = window.confirm(
      'Reset local Alberto workspace data on this browser? This clears records, reports, tasks, and uploads, but keeps your user account/session, personal profile, and OpenRouter key/model.'
    )
    if (!shouldReset) {
      return
    }

    setIsResetting(true)
    try {
      onResetLocalStorage()
      setStatusTone('success')
      setStatusMessage('Local workspace reset complete. User, personal profile, and OpenRouter settings were preserved.')
    } catch (error) {
      setStatusTone('danger')
      setStatusMessage(error instanceof Error ? error.message : 'Failed to reset local workspace data.')
    } finally {
      setIsResetting(false)
    }
  }

  function handleDeleteAllChats() {
    const shouldDelete = window.confirm(
      'Delete all chat threads on this browser? This cannot be undone.'
    )
    if (!shouldDelete) {
      return
    }

    setIsDeletingAllChats(true)
    try {
      onDeleteAllChats()
      setStatusTone('success')
      setStatusMessage('All chat threads deleted. Start a new conversation from the Chat tab.')
    } catch (error) {
      setStatusTone('danger')
      setStatusMessage(error instanceof Error ? error.message : 'Failed to delete chat threads.')
    } finally {
      setIsDeletingAllChats(false)
    }
  }

  return (
    <div className="settings-grid">
      {statusMessage && (
        <NeutralPanel
          tone="info"
          className="settings-status-banner px-4 py-3 text-sm/6"
          aria-live="polite"
        >
          {statusTone === 'info' ? (
            <div className="flex items-center gap-2">
              <span className={uiClasses.textSubtle}>{statusMessage}</span>
            </div>
          ) : (
            <StatusText as="div" className="flex items-center gap-2" tone={statusTone === 'danger' ? 'danger' : 'success'}>
              {statusTone === 'danger' ? (
                <ExclamationTriangleIcon className="inline size-4" />
              ) : (
                <CheckIcon className="inline size-4" />
              )}
              <span>
                {statusMessage}
              </span>
            </StatusText>
          )}
        </NeutralPanel>
      )}

      <section className="settings-main-section settings-section">
        <form onSubmit={handleSaveProfile} className="mt-5">
          <Fieldset>
            <FieldGroup>
              <div className="settings-form-group">
                <Eyebrow className="settings-form-group-title" variant="telemetry" tone="subtle">Identity</Eyebrow>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Field>
                    <Label htmlFor="profile-full-name">Full name</Label>
                    <Input
                      id="profile-full-name"
                      name="profile-full-name"
                      value={profileDraft.fullName}
                      onChange={(event) => updateProfileField('fullName', event.target.value)}
                      required
                    />
                  </Field>
                  <Field>
                    <Label htmlFor="profile-preferred-name">Preferred name</Label>
                    <Input
                      id="profile-preferred-name"
                      name="profile-preferred-name"
                      placeholder="Optional"
                      value={profileDraft.preferredName}
                      onChange={(event) => updateProfileField('preferredName', event.target.value)}
                    />
                  </Field>
                  <Field>
                    <Label htmlFor="profile-date-of-birth">Date of birth</Label>
                    <Input
                      id="profile-date-of-birth"
                      name="profile-date-of-birth"
                      type="date"
                      value={profileDraft.dateOfBirth}
                      onChange={(event) => updateProfileField('dateOfBirth', event.target.value)}
                    />
                  </Field>
                  <Field>
                    <Label htmlFor="profile-insurance-provider">Insurance provider</Label>
                    <Select
                      value={insuranceProviderSelectValue || undefined}
                      onValueChange={(nextValue) =>
                        handleInsuranceProviderSelect(
                          nextValue === INSURANCE_PROVIDER_NONE ? '' : nextValue
                        )
                      }
                    >
                      <SelectTrigger id="profile-insurance-provider">
                        <SelectValue placeholder="Select a provider (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={INSURANCE_PROVIDER_NONE}>No provider selected</SelectItem>
                        {INSURANCE_PROVIDER_OPTIONS.map((provider) => (
                          <SelectItem key={provider} value={provider}>
                            {provider}
                          </SelectItem>
                        ))}
                        <SelectItem value={INSURANCE_PROVIDER_OTHER}>Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  {isInsuranceProviderOtherSelected && (
                    <Field className="sm:col-span-2">
                      <Label htmlFor="profile-insurance-provider-other">Insurance provider (manual entry)</Label>
                      <Input
                        id="profile-insurance-provider-other"
                        name="profile-insurance-provider-other"
                        placeholder="Enter your insurance provider"
                        value={insuranceProviderCustomValue}
                        onChange={(event) => updateProfileField('insuranceProvider', event.target.value)}
                      />
                    </Field>
                  )}
                </div>
              </div>

              <div className="settings-form-group">
                <Eyebrow className="settings-form-group-title" variant="telemetry" tone="subtle">Contact</Eyebrow>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Field>
                    <Label htmlFor="profile-email">Account email</Label>
                    <Input
                      id="profile-email"
                      name="profile-email"
                      value={accountEmail}
                      readOnly
                    />
                  </Field>
                  <Field>
                    <Label htmlFor="profile-phone">Phone</Label>
                    <Input
                      id="profile-phone"
                      name="profile-phone"
                      placeholder="(555) 555-1234"
                      value={profileDraft.phone}
                      onChange={(event) => updateProfileField('phone', event.target.value)}
                    />
                  </Field>
                </div>
              </div>

              <div className="settings-form-group">
                <Eyebrow className="settings-form-group-title" variant="telemetry" tone="subtle">Address</Eyebrow>
                <div className="grid grid-cols-1 gap-3">
                  <Field>
                    <Label htmlFor="profile-address-line-1">Address line 1</Label>
                    <Input
                      id="profile-address-line-1"
                      name="profile-address-line-1"
                      placeholder="Street address"
                      value={profileDraft.addressLine1}
                      onChange={(event) => updateProfileField('addressLine1', event.target.value)}
                    />
                  </Field>
                  <Field>
                    <Label htmlFor="profile-address-line-2">Address line 2</Label>
                    <Input
                      id="profile-address-line-2"
                      name="profile-address-line-2"
                      placeholder="Apartment, suite, unit, etc."
                      value={profileDraft.addressLine2}
                      onChange={(event) => updateProfileField('addressLine2', event.target.value)}
                    />
                  </Field>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Field>
                      <Label htmlFor="profile-city">City</Label>
                      <Input
                        id="profile-city"
                        name="profile-city"
                        value={profileDraft.city}
                        onChange={(event) => updateProfileField('city', event.target.value)}
                      />
                    </Field>
                    <Field>
                      <Label htmlFor="profile-state">State / region</Label>
                      <Input
                        id="profile-state"
                        name="profile-state"
                        value={profileDraft.state}
                        onChange={(event) => updateProfileField('state', event.target.value)}
                      />
                    </Field>
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Field>
                      <Label htmlFor="profile-postal-code">Postal code</Label>
                      <Input
                        id="profile-postal-code"
                        name="profile-postal-code"
                        value={profileDraft.postalCode}
                        onChange={(event) => updateProfileField('postalCode', event.target.value)}
                      />
                    </Field>
                    <Field>
                      <Label htmlFor="profile-country">Country</Label>
                      <Input
                        id="profile-country"
                        name="profile-country"
                        value={profileDraft.country}
                        onChange={(event) => updateProfileField('country', event.target.value)}
                      />
                    </Field>
                  </div>
                </div>
              </div>

              <div className="settings-form-group">
                <Eyebrow className="settings-form-group-title" variant="telemetry" tone="subtle">Emergency contact</Eyebrow>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Field>
                    <Label htmlFor="profile-emergency-contact-name">Emergency contact name</Label>
                    <Input
                      id="profile-emergency-contact-name"
                      name="profile-emergency-contact-name"
                      value={profileDraft.emergencyContactName}
                      onChange={(event) => updateProfileField('emergencyContactName', event.target.value)}
                    />
                  </Field>
                  <Field>
                    <Label htmlFor="profile-emergency-contact-phone">Emergency contact phone</Label>
                    <Input
                      id="profile-emergency-contact-phone"
                      name="profile-emergency-contact-phone"
                      value={profileDraft.emergencyContactPhone}
                      onChange={(event) => updateProfileField('emergencyContactPhone', event.target.value)}
                    />
                  </Field>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  type="submit"
                  className=""
                  disabled={isSaving || isClearing || isDeletingAllChats || isResetting || isSavingProfile || !isProfileDirty}
                >
                  <CheckIcon data-slot="icon" />
                  {isSavingProfile ? 'Saving...' : 'Save personal info'}
                </Button>
              </div>
              {isProfileDirty && (
                <p className={`text-xs/5 ${uiClasses.textSubtle}`}>You have unsaved personal info changes.</p>
              )}
            </FieldGroup>
          </Fieldset>
        </form>
      </section>

      <section className="settings-main-section settings-section">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Subheading variant="dashboard">Appearance</Subheading>
          <Badge variant="zinc">
            This browser
          </Badge>
        </div>

        <Text className={uiClasses.bodyMuted}>
          Choose how Alberto Health should look on this browser.
        </Text>

        <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
          {THEME_OPTIONS.map((option) => {
            const isSelected = option.id === themePreference
            return (
              <Button
                key={option.id}
                type="button"
                variant={isSelected ? 'default' : 'outline'}
                className={isSelected ? 'settings-theme-option-active' : ''}
                onClick={() => onThemePreferenceChange(option.id)}
              >
                {option.label}
              </Button>
            )
          })}
        </div>
        <Eyebrow className="mt-2" variant="telemetry" tone="subtle">
          {THEME_OPTIONS.find((option) => option.id === themePreference)?.description}
        </Eyebrow>
      </section>

      <section className="settings-main-section settings-section">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Subheading variant="dashboard">LLM Settings</Subheading>
          <Badge variant={isConfigured ? 'emerald' : 'zinc'}>
            {isConfigured ? 'OpenRouter configured' : 'OpenRouter not configured'}
          </Badge>
        </div>

        <Text className={uiClasses.bodyMuted}>
          Add your OpenRouter key and model to power live AI responses in the chat tab.
          Settings are stored in local browser storage on this device.
        </Text>

        <form onSubmit={handleSubmit} className="mt-5">
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label htmlFor="openrouter-api-key">OpenRouter API key</Label>
                <Input
                  id="openrouter-api-key"
                  name="openrouter-api-key"
                  type="password"
                  autoComplete="off"
                  placeholder="sk-or-v1-..."
                  value={apiKeyDraft}
                  onChange={(event) => setApiKeyDraft(event.target.value)}
                />
              </Field>

              <Field>
                <Label htmlFor="openrouter-model">Model</Label>
                <Input
                  id="openrouter-model"
                  name="openrouter-model"
                  placeholder={DEFAULT_OPENROUTER_MODEL}
                  value={modelDraft}
                  onChange={(event) => setModelDraft(event.target.value)}
                  required
                />
              </Field>

              <div className="flex flex-wrap gap-2">
                <Button
                  type="submit"
                  className=""
                  disabled={isSaving || isClearing || isDeletingAllChats || isSavingProfile || !isLlmDirty}
                >
                  <CheckIcon data-slot="icon" />
                  {isSaving ? 'Saving...' : 'Save settings'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className=""
                  onClick={() => void handleClear()}
                  disabled={isSaving || isClearing || isDeletingAllChats || isSavingProfile || (!apiKeyDraft.trim() && !isConfigured)}
                >
                  <TrashIcon data-slot="icon" />
                  {isClearing ? 'Clearing...' : 'Clear key'}
                </Button>
              </div>
              {isLlmDirty && (
                <p className={`text-xs/5 ${uiClasses.textSubtle}`}>You have unsaved changes.</p>
              )}
            </FieldGroup>
          </Fieldset>
        </form>
      </section>

<section className="settings-main-section settings-section">
        <Subheading variant="dashboard">Danger Zone</Subheading>
        <Text className={uiClasses.bodyMuted}>
          Delete all chat threads, or reset local browser workspace data on this device while preserving user/session,
          personal profile, and OpenRouter settings.
        </Text>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            className=""
            onClick={onSignOut}
            disabled={isSaving || isClearing || isDeletingAllChats || isResetting || isSavingProfile}
          >
            <ArrowRightOnRectangleIcon data-slot="icon" />
            {signOutLabel}
          </Button>
          <Button
            type="button"
            variant="outline"
            className=""
            onClick={handleDeleteAllChats}
            disabled={isSaving || isClearing || isDeletingAllChats || isResetting || isSavingProfile}
          >
            <TrashIcon data-slot="icon" />
            {isDeletingAllChats ? 'Deleting...' : 'Delete all chats'}
          </Button>
          <Button
            type="button"
            variant="outline"
            className=""
            onClick={handleResetLocalStorage}
            disabled={isSaving || isClearing || isDeletingAllChats || isResetting || isSavingProfile}
          >
            <TrashIcon data-slot="icon" />
            {isResetting ? 'Resetting...' : 'Reset local storage'}
          </Button>
        </div>
      </section>
    </div>
  )
}
